import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

import { getProducts } from '../api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('tistore_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkingCart, setCheckingCart] = useState(false);
  const [cartError, setCartError] = useState('');
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const checkSequence = useRef(0);
  const validateCart = useCallback(async () => {
    const sequence = ++checkSequence.current;
    if (!cartRef.current.length) { setCheckingCart(false); setCartError(''); return true; }
    setCheckingCart(true);
    try {
      const products = await getProducts();
      if (sequence !== checkSequence.current) return false;
      const byId = new Map(products.filter(p => p.active !== false).map(p => [p.id, p]));
      let valid = true, changed = false;
      const latest = cartRef.current.map(item => {
        const product = byId.get(item.id);
        const unavailable = !product ? 'Товар больше недоступен' : !product.sizes.includes(item.size) ? 'Этот размер больше недоступен' : '';
        if (unavailable) { valid = false; return {...item, unavailable}; }
        if (item.price !== product.price) changed = true;
        return {...item, name: product.name, article: product.article, price: product.price, oldPrice: product.old_price, image: product.images?.[0] || item.image, unavailable: ''};
      });
      setCart(latest);
      setCartError(!valid ? 'Удалите недоступные товары, чтобы оформить заказ.' : changed ? 'Цены обновились. Проверьте итоговую сумму перед оформлением.' : '');
      return valid && !changed;
    } catch {
      if (sequence === checkSequence.current) setCartError('Не удалось проверить товары. Повторите проверку перед оформлением.');
      return false;
    } finally { if (sequence === checkSequence.current) setCheckingCart(false); }
  }, []);
  const cartKey = cart.map(item => `${item.id}:${item.size}:${item.quantity}`).join('|');
  useEffect(() => { validateCart(); }, [cartKey, isCartOpen, validateCart]);
  useEffect(() => {
    const refresh = () => validateCart();
    window.addEventListener('focus', refresh);
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 30000);
    return () => { window.removeEventListener('focus', refresh); clearInterval(timer); };
  }, [validateCart]);
  const hasUnavailable = cart.some(item => item.unavailable);


  useEffect(() => {
    localStorage.setItem('tistore_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, size, quantity = 1) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(
        item => item.id === product.id && item.size === size
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [...prev, {
        id: product.id,
        article: product.article,
        name: product.name,
        price: product.price,
        oldPrice: product.old_price,
        image: product.images?.[0] || '',
        size,
        quantity
      }];
    });
  };

  const removeFromCart = (id, size) => {
    setCart(prev => prev.filter(item => !(item.id === id && item.size === size)));
  };

  const updateQuantity = (id, size, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id, size);
      return;
    }
    setCart(prev => prev.map(item =>
      item.id === id && item.size === size
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.unavailable ? 0 : item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      checkingCart, cartError, validateCart, hasUnavailable,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
